import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { Plus, FileText, Lock, Users, User as UserIcon } from "lucide-react";
import ClientShell from "./ClientShell";
import { useWorkDocumentsList, useCreateWorkDocument } from "@/lib/workDocumentQueries";
import PromptDialog from "./presentation/PromptDialog";

const VISIBILITY_ICON = { workspace_shared: Users, member_private: UserIcon, coach_private: Lock };
const VISIBILITY_LABEL = { workspace_shared: "Shared", member_private: "Private", coach_private: "Coach only" };

export default function WorkDocumentList() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isPending } = useWorkDocumentsList(workspaceId);
  const createDocument = useCreateWorkDocument(workspaceId);
  const items = data?.items || [];

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="work-document-list-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-ink">Work Documents</h1>
          <button
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors"
            data-testid="work-document-list-create"
          >
            <Plus size={15} /> New document
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4" data-testid="work-document-list-items">
          {isPending && <p className="text-ink-soft text-sm" data-testid="work-document-list-loading">Loading documents…</p>}
          {items.map((d) => {
            const Icon = VISIBILITY_ICON[d.visibility_scope] || FileText;
            return (
              <button
                key={d.wdoc_id}
                onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${d.wdoc_id}`)}
                className="border border-ink/15 bg-white/50 rounded-lg p-5 text-left hover:border-terracotta transition-colors"
                data-testid={`work-document-list-item-${d.wdoc_id}`}
              >
                <div className="flex items-center gap-2 text-ink-soft mb-2">
                  <Icon size={14} /><span className="text-xs uppercase tracking-wide">{VISIBILITY_LABEL[d.visibility_scope] || d.visibility_scope}</span>
                </div>
                <p className="font-serif text-lg text-ink mb-1">{d.title}</p>
                <p className="text-xs text-ink-soft capitalize">{d.status}</p>
              </button>
            );
          })}
          {items.length === 0 && !isPending && <p className="text-ink-soft text-sm" data-testid="work-document-list-empty">No work documents yet.</p>}
        </div>
      </div>
      <PromptDialog
        open={dialogOpen} onOpenChange={setDialogOpen} title="Name your work document" placeholder="e.g. Coaching Workbook"
        confirmLabel="Create"
        onConfirm={(title) => createDocument.mutate(
          { document_type: "workbook", title, visibility_scope: "workspace_shared" },
          { onSuccess: (res) => navigate(`/rehearsal-room/w/${workspaceId}/work-documents/${res.wdoc_id}`) },
        )}
        testId="work-document-list-create-dialog"
      />
    </ClientShell>
  );
}
